/** A suggested alternative time slot returned by the booking API on a 409 conflict. */
export interface SlotAlternative {
  time: string
  availablePractitioners: number
}

export interface ApiErrorBody {
  success?: boolean
  // Some endpoints respond with `error` as a plain string (legacy shape); others use the
  // structured `{code, message}` (createError/AppError) shape. Both are real, live shapes —
  // callers must handle either, not assume the object form (dev merge, CartContext.tsx).
  error?:
    | string
    | {
        code?: string
        message?: string
        /** Present on a 409 booking conflict — suggested alternative slots (BO-012 AC3). */
        alternatives?: SlotAlternative[]
      }
  /** Top-level machine code (simplified-booking controller AppError shape). */
  code?: string
  message?: string
}

/**
 * Thrown when a booking cannot be created because the requested slot is no longer
 * available. Carries the backend's suggested alternative times so the caller can
 * offer them to the customer (BO-012 AC3) instead of a dead-end error toast.
 */
export class SlotUnavailableError extends Error {
  readonly code: string
  readonly alternatives: SlotAlternative[]

  constructor(message: string, alternatives: SlotAlternative[] = [], code = 'NOT_AVAILABLE') {
    super(message)
    this.name = 'SlotUnavailableError'
    this.code = code
    this.alternatives = alternatives
    // Preserve instanceof across the TS/Babel transpile-to-ES5 prototype flattening.
    Object.setPrototypeOf(this, SlotUnavailableError.prototype)
  }
}

/**
 * `body.error` is either a plain string (legacy shape) or the structured
 * `{code, message, alternatives}` (createError/AppError) shape — narrow to the
 * object form, or undefined if it's a string (a string has no .code/.alternatives).
 */
function errorObj(body?: ApiErrorBody | null) {
  return typeof body?.error === 'object' ? body.error : undefined
}

/**
 * A 409 booking conflict whose body carries suggested alternatives (BO-012 AC3).
 * Requires the exact `NOT_AVAILABLE` code — the mere presence of an `alternatives`
 * array is not sufficient, since a different 409 (e.g. a room/therapist conflict
 * with its own distinct code) could also carry one and would be misrouted into
 * the slot-picker instead of its own error path.
 */
export function isSlotUnavailable(status: number, body?: ApiErrorBody | null): boolean {
  return status === 409 && errorObj(body)?.code === 'NOT_AVAILABLE'
}

export function isPermissionDenied(status: number, body?: ApiErrorBody | null): boolean {
  return status === 403 || errorObj(body)?.code === 'INSUFFICIENT_PERMISSIONS'
}

/** INV-007 — server rejected add/pay because tracked product stock is insufficient (409). */
export function isInsufficientStock(status: number, body?: ApiErrorBody | null): boolean {
  return status === 409 && errorObj(body)?.code === 'INSUFFICIENT_STOCK'
}

/** Staff-facing stock error — BO-024 AC4 wording when API returns INSUFFICIENT_STOCK. */
export function getInsufficientStockMessage(body?: ApiErrorBody | null): string {
  const apiMessage = getApiErrorMessage(body, '')
  if (apiMessage) return apiMessage
  return 'Product out of stock.'
}

/** Error thrown by selling API client when stock check fails at add or pay. */
export function isInsufficientStockError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: string }
  return e.code === 'INSUFFICIENT_STOCK'
}

/** POS toast text — prefer INSUFFICIENT_STOCK message, else fallback. */
export function getPosErrorMessage(error: unknown, fallback: string): string {
  if (isInsufficientStockError(error)) {
    const message = (error as Error).message
    return message || 'Product out of stock.'
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/**
 * True only when a 401 means the session is invalid/expired.
 * Misclassified business errors (e.g. missing tenant context) must NOT clear the session.
 */
export function isSessionAuthFailure(status: number, body?: ApiErrorBody | null): boolean {
  if (status !== 401) return false

  const code = (errorObj(body)?.code || '').toUpperCase()
  const message = (getApiErrorMessage(body, '') || '').toLowerCase()

  if (
    code === 'INSUFFICIENT_PERMISSIONS' ||
    code === 'TENANT_ID_REQUIRED' ||
    code === 'TENANT_CONTEXT_REQUIRED' ||
    message.includes('tenant id required') ||
    message.includes('insufficient permissions')
  ) {
    return false
  }

  return true
}

/**
 * Extracts a human-readable message regardless of which `error` shape the endpoint
 * returned — a plain string, or the structured `{code, message}` object (dev merge:
 * passing the object itself into `new Error()` stringifies to "[object Object]").
 */
export function getApiErrorMessage(
  body?: ApiErrorBody | null,
  fallback = 'Something went wrong'
): string {
  if (typeof body?.error === 'string') return body.error
  return errorObj(body)?.message || body?.message || fallback
}

/**
 * BO-047 R2: friendly, actionable copy for voucher-add failures. The server
 * enforces the 3-distinct-types limit per order; when it fires, tell staff
 * what to do next instead of the generic "Failed to add voucher".
 */
export function getVoucherAddErrorMessage(
  body?: ApiErrorBody | null,
  fallback = 'Failed to add voucher'
): string {
  const code = body?.code || errorObj(body)?.code
  if (code === 'VOUCHER_TYPE_LIMIT_EXCEEDED') {
    return 'A maximum of 3 different voucher types can be sold in one transaction — remove one from the cart or complete this sale first'
  }
  return getApiErrorMessage(body, fallback)
}

/** Selling API errors — includes legacy `details` and top-level `message` fallbacks. */
export function getSellingApiErrorMessage(
  body?: ApiErrorBody | null,
  fallback = 'Request failed'
): string {
  const fromError = getApiErrorMessage(body, '')
  if (fromError) return fromError
  const obj = errorObj(body) as { details?: string } | undefined
  if (obj?.details) return String(obj.details)
  if (body?.message) return body.message
  return fallback
}

export type ParsedApiResponse<T> =
  | { ok: true; data: T }
  | {
      ok: false
      status: number
      body: ApiErrorBody | null
      message: string
      isPermissionDenied: boolean
    }

export async function parseApiResponse<T>(response: Response): Promise<ParsedApiResponse<T>> {
  const body = (await response.json().catch(() => null)) as T | ApiErrorBody | null

  if (response.ok) {
    return { ok: true, data: body as T }
  }

  const errorBody = body as ApiErrorBody | null

  return {
    ok: false,
    status: response.status,
    body: errorBody,
    message: getApiErrorMessage(errorBody, response.statusText || 'Request failed'),
    isPermissionDenied: isPermissionDenied(response.status, errorBody),
  }
}

export const PERMISSION_DENIED_MESSAGE =
  'You do not have permission to access this page. Please contact your tenant administrator if you need access.'
